import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-scroll the page at a chosen speed using requestAnimationFrame.
 *
 * Why RAF (not setInterval): syncs with the display refresh, so scroll
 * is smooth on 60 / 120 / 144 Hz screens. setInterval drifts and
 * stutters under load.
 *
 * Speed semantics — pixels per second:
 *   1x = 24  → slow contemplative reading pace
 *   2x = 48  → medium cruise
 *   3x = 96  → faster (skim a familiar surah)
 *
 * Honors `prefers-reduced-motion: reduce` — when set, all speeds are
 * halved. We deliberately keep the feature available rather than
 * hiding it; the user opted in by clicking. Halving is enough to
 * remove the "ticker tape" sensation that triggers the OS preference.
 *
 * Cleanup paths (all converge on cancelling the RAF):
 *   - component unmount / route change → useEffect return
 *   - speed set to 'off'                → effect re-runs, cancels old frame
 *   - page reaches bottom               → tick() flips speed to 'off'
 *   - tab hidden (visibilitychange)     → pause; resume on visible
 *
 * State lives here; the consuming component just renders the trigger
 * and dropdown.
 */

export type AutoScrollSpeed = 'off' | '0.5x' | '1x' | '2x' | '3x';

// Tuned by hand. Each notch is clearly distinct:
//   0.5x = 30  → very slow contemplative pace (a line every ~1s)
//   1x   = 60  → slow reading
//   2x   = 120 → comfortable cruise
//   3x   = 240 → skim a familiar surah
const PX_PER_SEC: Record<Exclude<AutoScrollSpeed, 'off'>, number> = {
  '0.5x': 30,
  '1x': 60,
  '2x': 120,
  '3x': 240,
};

// How many CSS pixels of slack we allow before declaring "bottom reached".
// 4px tolerates fractional pixel rounding without ever falsely matching
// mid-page.
const BOTTOM_EPSILON = 4;

export function useAutoScroll() {
  const [speed, setSpeedState] = useState<AutoScrollSpeed>('off');

  // Live refs so the RAF tick closure always reads current values
  // without us having to recreate the closure on every state change
  // (which would also cancel/re-schedule the frame and introduce jitter).
  const speedRef = useRef<AutoScrollSpeed>('off');
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const reducedMotionRef = useRef<boolean>(false);
  // Sub-pixel accumulator. At 0.5x (30 px/sec) and 60 Hz, each frame
  // wants to advance by ~0.48 px — but window.scrollBy() floors that
  // to 0 and the page never moves. We accumulate fractional pixels
  // here and only call scrollBy when at least one whole pixel has
  // built up. The leftover stays in the accumulator for the next frame.
  const accumulatedRef = useRef<number>(0);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  // Observe the OS reduced-motion preference. Live updates so users can
  // toggle the setting mid-scroll and feel the change.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const cancelFrame = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTsRef.current = null;
    accumulatedRef.current = 0;
  }, []);

  const setSpeed = useCallback((next: AutoScrollSpeed) => {
    setSpeedState(next);
  }, []);

  // The single RAF tick function. Stored in a ref so the visibility
  // effect can re-arm it on tab return without recreating the closure.
  const tickRef = useRef<((now: number) => void) | null>(null);

  useEffect(() => {
    const tick = (now: number) => {
      if (speedRef.current === 'off') {
        cancelFrame();
        return;
      }

      // First frame after start (or after tab resume): seed lastTs so
      // the next frame's dt is a real delta, not a multi-second jump.
      if (lastTsRef.current === null) {
        lastTsRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const dt = now - lastTsRef.current;
      lastTsRef.current = now;

      const basePxPerSec =
        PX_PER_SEC[speedRef.current as Exclude<AutoScrollSpeed, 'off'>];
      const pxPerSec = reducedMotionRef.current
        ? basePxPerSec / 2
        : basePxPerSec;
      const delta = pxPerSec * (dt / 1000);

      // Check bottom BEFORE scrolling so the final frame lands exactly
      // at the bottom, then we stop.
      const docEl = document.documentElement;
      const atBottom =
        window.scrollY + window.innerHeight >=
        docEl.scrollHeight - BOTTOM_EPSILON;

      if (atBottom) {
        setSpeedState('off');
        cancelFrame();
        return;
      }

      // Accumulate sub-pixel fractions; only scroll when a whole pixel
      // has built up. Without this, slow speeds (0.5x / 1x) round to
      // zero per frame and the page never moves.
      accumulatedRef.current += delta;
      if (accumulatedRef.current >= 1) {
        const whole = Math.floor(accumulatedRef.current);
        accumulatedRef.current -= whole;
        window.scrollBy({ top: whole, left: 0, behavior: 'auto' });
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    tickRef.current = tick;

    if (speed === 'off') {
      cancelFrame();
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
    return cancelFrame;
  }, [speed, cancelFrame]);

  // Pause on tab hidden, resume on visible (if speed was non-off).
  // Cleanly cancels in-flight RAFs and starts a fresh one so the
  // first frame has a clean baseline.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        cancelFrame();
        return;
      }
      // visible: if user had auto-scroll on, restart the loop.
      if (speedRef.current !== 'off' && tickRef.current) {
        lastTsRef.current = null;
        rafRef.current = requestAnimationFrame(tickRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [cancelFrame]);

  // Global Escape key — stops auto-scroll regardless of whether the
  // Global Escape key — stops auto-scroll regardless of whether the
  // dropdown is open. Lets the user kill it instantly without having to
  // hunt for the button mid-scroll. Ignored when typing in inputs.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (speedRef.current === 'off') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }
      setSpeedState('off');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return {
    speed,
    setSpeed,
    isActive: speed !== 'off',
  };
}
